import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';

import { navigationPaths } from './constants/navigationPaths';
import { PermissionsList } from './constants/permissionsList';
import AdminToolContainer from './containers/AdminToolContainers';
import AuditViewContainer from './containers/AuditViewContainer';
import GlobalViewContainer from './containers/GlobalViewContainer';
import LocationViewContainer from './containers/LocationViewContainer';
import TurbineViewContainer from './containers/TurbineViewContainer';
import UserProfileContainer from './containers/UserProfileContainer';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import ProtectedRoute from '@/components/ProtectedRoute';
import HistoryContainer from '@/containers/HistoryContainer';
import ChangePasswordContainer from '@/containers/LoginContainers/ChangePassword';
import ForgotPasswordContainer from '@/containers/LoginContainers/ForgotPassword';
import LoginContainer from '@/containers/LoginContainers/Login';
import PasswordRecoveryContainer from '@/containers/LoginContainers/PasswordRecovery';
import SiteViewContainer from '@/containers/SiteViewContainer';
import useIsSignedIn from '@/hooks/useIsSignedIn';
import AdminToolLayout from '@/layouts/AdminToolLayout';
import AuthLayout from '@/layouts/AuthLayout';
import HomeLayout from '@/layouts/HomeLayout';
import { RootState } from '@/store';
import { adminToolRoutes } from '@/utils/adminTool';

function DefaultRedirect() {
  const auditableState = useSelector(
    (state: RootState) => state.auditableElements
  );
  const auditableElements = auditableState?.authorizations
    ?.permissions as PermissionsList;

  const permissions = auditableElements || {};
  const mapListViewAllowed = permissions?.['MAP_LIST_VIEW'];
  const siteSummaryAllowed = permissions?.['SITE_SUMMARY'];
  const hasPermissions =
    auditableElements && Object.keys(auditableElements).length > 0;
  const permissionsLoading = auditableState?.status?.message === 'loading';
  const permissionsFailed = auditableState?.status?.message === 'failed';

  if (permissionsLoading || (!hasPermissions && !permissionsFailed)) {
    return <ContainerLoader />;
  }

  if (mapListViewAllowed) {
    return <Navigate to="/turbines" replace />;
  }

  if (siteSummaryAllowed) {
    return <Navigate to="/locations" replace />;
  }

  // Signed in with no landing permission — keep app shell, do not loop routes.
  return null;
}

function AppRoutes() {
  const userIsSignedIn = useIsSignedIn();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const permissions = auditableElements || {};
  const adminToolAllowed = userIsSignedIn && permissions?.['ADMIN_TOOL'];
  const mapListViewAllowed = userIsSignedIn && permissions?.['MAP_LIST_VIEW'];
  const siteSummaryAllowed = userIsSignedIn && permissions?.['SITE_SUMMARY'];

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="login" element={<LoginContainer />} />
        <Route path="forgot-password" element={<ForgotPasswordContainer />} />
        <Route
          path="password-recovery"
          element={<PasswordRecoveryContainer />}
        />
        <Route path="change-password" element={<ChangePasswordContainer />} />
      </Route>

      <Route
        element={
          <ProtectedRoute isAllowed={userIsSignedIn} redirectPath="/login">
            <HomeLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DefaultRedirect />} />
        <Route path="profile" element={<UserProfileContainer />} />
      </Route>

      <Route
        element={
          <ProtectedRoute isAllowed={mapListViewAllowed} redirectPath="/">
            <HomeLayout />
          </ProtectedRoute>
        }
      >
        <Route path="turbines" element={<GlobalViewContainer />} />
        <Route
          path={navigationPaths.turbinesDetailsTab}
          element={<TurbineViewContainer />}
        />
      </Route>

      <Route
        element={
          <ProtectedRoute isAllowed={siteSummaryAllowed} redirectPath="/">
            <HomeLayout />
          </ProtectedRoute>
        }
      >
        <Route path="locations" element={<SiteViewContainer />} />
        <Route
          path={navigationPaths.locationsDetails.replace(':siteId', ':siteId')}
          element={<LocationViewContainer />}
        />
        <Route path="audit" element={<AuditViewContainer />} />
        <Route path="history" element={<HistoryContainer />} />
      </Route>

      <Route
        element={
          <ProtectedRoute isAllowed={adminToolAllowed} redirectPath="/">
            <AdminToolLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="admin-tool"
          element={<Navigate to="/admin-tool/users" replace />}
        />

        {adminToolRoutes.map(path => (
          <Route key={path} path={path} element={<AdminToolContainer />} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default AppRoutes;
